import React, { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import excursionDetailStyles from "../../asset/styles/excursionDetail/excursionDetail.module.scss"
import Grid from "@mui/material/Grid"
import ExcursionBasicDetail from "./excursionBasicDetail"
import ExcursionBlackOutDate from "./excursionBlackOutDate"
import ExcursionChildPolicy from "./excursionChildPolicy"
import ExcursionPricing from "./excursionPricing"
import ExcursionAddOns from "./excursionAddOns"
import Button from "component/button/button"
import { agent } from "apiClient/api"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { useSnackbar } from "../../context/snackbarContext"
import {
    ExcursionDetailModel,
    ExcursionPutPayload,
    ExcursionPutPricingItem,
    ExcursionPutAddOnItem,
    ExcursionPutAddOnPricingItem,
    ExcursionPutBlackoutDateItem,
    ExcursionPutUnavailableDayItem,
    ExcursionPutChildPolicyItem,
} from "../../models"
import { toTitleCase } from "util/textcase"
import { getActiveCityOptions } from "util/cityOptionsUtil"

export interface ExcursionValidationErrors {
    excursion_name?: string
    city?: string
    blackout?: string
    blackout_overlap?: string
    blackout_invalid_range?: string
    unavailable_days?: string
    child_policy_max_age?: string
    pricing_base_price?: string
    add_ons?: string
}

function validateExcursion(data: ExcursionDetailModel): ExcursionValidationErrors {
    const errors: ExcursionValidationErrors = {}

    // Basic: Excursion Name and City required; Type not required
    if (!(data.excursion_name ?? "").trim()) {
        errors.excursion_name = "Excursion Name is required"
    }
    if (!data.city_id && !(data.city_name ?? "").trim()) {
        errors.city = "City is required"
    }

    // Blackout (only when Enable Blackout Dates is checked)
    if (data.flag_enable_blackout_dates) {
        const hasDateRow = (data.blackout_dates ?? []).some(
            (b) => (b.start_date ?? "").trim() !== "" && (b.end_date ?? "").trim() !== ""
        )
        const hasUnavailableDaysYes = data.flag_excursion_not_available_on_days === true
        const hasDaySelected = (data.excursion_unavailable_days ?? []).length > 0

        if (!hasDateRow && !(hasUnavailableDaysYes && hasDaySelected)) {
            errors.blackout =
                "Either add at least one date range (Start & End) or set 'Are there any days when this excursion is unavailable?' to Yes and select at least one day"
        }
        if (hasUnavailableDaysYes && !hasDaySelected) {
            errors.unavailable_days = "At least one day must be selected when excursion is unavailable on certain days"
        }

        // Start date must be before or equal to end date for each row
        const blackoutDates = data.blackout_dates ?? []
        for (let i = 0; i < blackoutDates.length; i++) {
            const row = blackoutDates[i]
            if ((row.start_date ?? "").trim() === "" || (row.end_date ?? "").trim() === "") continue
            const startT = new Date(row.start_date).getTime()
            const endT = new Date(row.end_date).getTime()
            if (isNaN(startT) || isNaN(endT)) continue
            if (startT > endT) {
                errors.blackout_invalid_range = "Start date must be before or equal to end date"
                break
            }
        }

        // Multiple dates cannot overlap
        const filledRanges = blackoutDates.filter(
            (b) => (b.start_date ?? "").trim() !== "" && (b.end_date ?? "").trim() !== ""
        )
        for (let i = 0; i < filledRanges.length; i++) {
            const a = filledRanges[i]
            const startA = new Date(a.start_date).getTime()
            const endA = new Date(a.end_date).getTime()
            if (isNaN(startA) || isNaN(endA) || endA < startA) continue
            for (let j = i + 1; j < filledRanges.length; j++) {
                const b = filledRanges[j]
                const startB = new Date(b.start_date).getTime()
                const endB = new Date(b.end_date).getTime()
                if (isNaN(startB) || isNaN(endB) || endB < startB) continue
                if (startA <= endB && startB <= endA) {
                    errors.blackout_overlap = "Blackout date ranges cannot overlap"
                    break
                }
            }
            if (errors.blackout_overlap) break
        }
    }

    // Child Policy: Maximum Age is required for all rows (Child and Infant; must be > 0)
    const childPolicy = data.child_policy ?? []
    for (let i = 0; i < childPolicy.length; i++) {
        const row = childPolicy[i]
        const maxAge = row.max_age
        if (
            maxAge === undefined ||
            maxAge === null ||
            !Number.isFinite(maxAge) ||
            maxAge < 0
        ) {
            errors.child_policy_max_age = "Maximum Age is required for all Child Policy rows"
            break
        }
        if (maxAge <= 0) {
            errors.child_policy_max_age =
                "Maximum Age is required for all Child Policy rows (enter a value greater than 0)"
            break
        }
    }

    // Pricing: Base Price is required (must be > 0; 0 is treated as empty)
    const pricing = data.excursion_pricing ?? []
    for (let i = 0; i < pricing.length; i++) {
        const row = pricing[i]
        const basePrice = row.base_price
        if (
            basePrice === undefined ||
            basePrice === null ||
            !Number.isFinite(basePrice) ||
            basePrice < 0
        ) {
            errors.pricing_base_price = "Base Price is required for all Pricing rows"
            break
        }
        if (basePrice < 0) {
            errors.pricing_base_price = "Base Price is required (enter a value greater than 0)"
            break
        }
    }

    // Add Ons: if Add-on Name is written, all corresponding prices are required (must be > 0)
    const addOns = data.excursion_add_ons ?? []
    for (let i = 0; i < addOns.length; i++) {
        const addOn = addOns[i]
        const name = (addOn.add_on_name ?? "").trim()
        if (name === "") continue
        const prices = addOn.add_on_pricing ?? []
        const missing = prices.some(
            (p) =>
                p.price === undefined ||
                p.price === null ||
                !Number.isFinite(p.price) ||
                p.price < 0
        )
        if (missing) {
            errors.add_ons = "When Add-on Name is entered, all prices for that add-on are required"
            break
        }
        const zeroPrice = prices.some((p) => (p.price ?? 0) <= 0)
        if (zeroPrice) {
            errors.add_ons = "When Add-on Name is entered, all prices must be filled (greater than 0)"
            break
        }
    }

    return errors
}

function hasValidationErrors(errors: ExcursionValidationErrors): boolean {
    return Object.keys(errors).length > 0
}

/** Normalize API error (object with code/message or string) to a string for display */
function getErrorMessage(error: unknown, fallback: string): string {
    if (error == null) return fallback
    if (typeof error === "string") return error
    if (
        typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof (error as { message: unknown }).message === "string"
    ) {
        return (error as { message: string }).message
    }
    return fallback
}

/** Look up category_id from generic categories by name (case-insensitive). Returns 0 if not found. */
function getCategoryId(
    categoryName: string,
    categories: { category_id: number; category_name: string }[]
): number {
    const key = (categoryName ?? "").trim().toLowerCase()
    const found = categories.find(
        (c) => (c.category_name ?? "").trim().toLowerCase() === key
    )
    return found?.category_id ?? 0
}

/** Default rows for new excursion; category_id from GenericGlobalAPI.category when provided */
function getEmptyExcursion(
    categories: { category_id: number; category_name: string }[] = []
): ExcursionDetailModel {
    const adultId = getCategoryId("adult", categories)
    const childId = getCategoryId("child", categories)
    const infantId = getCategoryId("infant", categories)
    return {
        excursion_id: "",
        excursion_name: "",
        city_id: 0,
        city_name: "",
        excursion_type_id: 0,
        excursion_type_name: "",
        flag_enable_blackout_dates: false,
        flag_excursion_not_available_on_days: false,
        flag_active: true,
        flag_default: false,
        excursion_pricing: [
            { excursion_pricing_id: "new-adult", excursion_id: "", category_id: adultId, category_name: "Adult", base_price: 0 },
            { excursion_pricing_id: "new-child", excursion_id: "", category_id: childId, category_name: "Child", base_price: 0 },
        ],
        excursion_add_ons: [
            {
                excursion_add_on_id: "",
                excursion_id: "",
                add_on_name: "",
                add_on_pricing: [
                    { add_on_pricing_id: "", excursion_add_on_id: "", category_id: adultId, category_name: "Adult", price: 0 },
                    { add_on_pricing_id: "", excursion_add_on_id: "", category_id: childId, category_name: "Child", price: 0 },
                ],
            },
        ],
        blackout_dates: [],
        excursion_unavailable_days: [],
        child_policy: [
            { child_policy_id: "new-child", excursion_id: "", category_id: childId, category_name: "Child", flag_free_of_charge: false, max_age: 0, max_height: 0 },
            { child_policy_id: "new-infant", excursion_id: "", category_id: infantId, category_name: "Infant", flag_free_of_charge: true, max_age: 0, max_height: 0 },
        ],
    }
}

/** Patch category_id 0 in excursion with ids from generic categories (match by category_name) */
function patchExcursionCategoryIds(
    data: ExcursionDetailModel,
    categories: { category_id: number; category_name: string }[]
): ExcursionDetailModel {
    if (!categories.length) return data
    const patch = (name: string, currentId: number) =>
        currentId === 0 ? getCategoryId(name, categories) : currentId
    return {
        ...data,
        excursion_pricing: data.excursion_pricing.map((p) => ({
            ...p,
            category_id: patch(p.category_name ?? "", p.category_id),
        })),
        excursion_add_ons: data.excursion_add_ons.map((a) => ({
            ...a,
            add_on_pricing: (a.add_on_pricing ?? []).map((ap) => ({
                ...ap,
                category_id: patch(ap.category_name ?? "", ap.category_id),
            })),
        })),
        child_policy: data.child_policy.map((c) => ({
            ...c,
            category_id: patch(c.category_name ?? "", c.category_id),
        })),
    }
}

/** True if ID is a client placeholder (empty or "new-*"), should be sent as null when creating */
function isPlaceholderId(id: string | undefined | null): boolean {
    if (id == null || id === "") return true
    return id.startsWith("new-")
}

function toPutPayload(data: ExcursionDetailModel, isNew: boolean): ExcursionPutPayload {
    return {
        excursion_id: isNew ? null : data.excursion_id || null,
        excursion_name: data.excursion_name,
        city_id: data.city_id,
        city_name: data.city_name,
        excursion_type_id: data.excursion_type_id,
        excursion_type_name: data.excursion_type_name,
        flag_enable_blackout_dates: data.flag_enable_blackout_dates,
        flag_excursion_not_available_on_days: data.flag_excursion_not_available_on_days,
        flag_active: data.flag_active,
        flag_default: data.flag_default ?? false,
        excursion_pricing: data.excursion_pricing.map(
            (p): ExcursionPutPricingItem => ({
                excursion_pricing_id: isNew && isPlaceholderId(p.excursion_pricing_id) ? null : (p.excursion_pricing_id || null),
                category_id: p.category_id,
                category_name: p.category_name,
                base_price: p.base_price,
            })
        ),
        excursion_add_ons: data.excursion_add_ons
            .filter((a) => (a.add_on_name ?? "").trim() !== "")
            .map(
                (a): ExcursionPutAddOnItem => ({
                    excursion_add_on_id: isNew && isPlaceholderId(a.excursion_add_on_id) ? null : (a.excursion_add_on_id || null),
                    add_on_name: a.add_on_name,
                    flag_default: a.flag_default ?? false,
                    add_on_pricing: a.add_on_pricing.map(
                        (ap): ExcursionPutAddOnPricingItem => ({
                            add_on_pricing_id: isNew && isPlaceholderId(ap.add_on_pricing_id) ? null : (ap.add_on_pricing_id || null),
                            category_id: ap.category_id,
                            category_name: ap.category_name,
                            price: ap.price,
                        })
                    ),
                })
            ),
        blackout_dates: data.blackout_dates.map(
            (b): ExcursionPutBlackoutDateItem => ({
                blackout_dates_id: isNew ? null : (b.blackout_dates_id || null),
                start_date: b.start_date,
                end_date: b.end_date,
            })
        ),
        excursion_unavailable_days: data.excursion_unavailable_days.map(
            (u): ExcursionPutUnavailableDayItem => ({
                excursion_unavailable_days_id: isNew ? null : (u.excursion_unavailable_days_id || null),
                week_day_id: u.week_day_id,
                week_day_name: u.week_day_name,
            })
        ),
        child_policy: data.child_policy.map(
            (c): ExcursionPutChildPolicyItem => ({
                child_policy_id: isNew && isPlaceholderId(c.child_policy_id) ? null : (c.child_policy_id || null),
                category_id: c.category_id,
                category_name: c.category_name,
                flag_free_of_charge: c.flag_free_of_charge,
                max_age: c.max_age,
                max_height: c.max_height,
            })
        ),
    }
}

const ExcursionDetail: React.FC = () => {
    const { excursion_id } = useParams<{ excursion_id: string }>()
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbar()
    const { hotelGenericData, setHotelGenericData } = useHotelGeneric()

    const [excursion, setExcursion] = useState<ExcursionDetailModel | null>(null)
    const [loading, setLoading] = useState(true)
    const [validationErrors, setValidationErrors] = useState<ExcursionValidationErrors>({})

    const screenWidth = typeof window !== "undefined" ? window.innerWidth : 1024
    const screenSize =
        screenWidth >= 2560 ? "80%" : screenWidth >= 1440 ? "70%" : screenWidth >= 1024 ? "90%" : "100%"

    const fetchGeneric = useCallback(async () => {
        try {
            const response = await agent.GenericGlobalAPI.get()
            if (response?.status !== false && response?.data?.[0]) {
                setHotelGenericData(response.data[0])
            }
        } catch (e) {
            console.error("GetGenericHotelDetails Error", e)
        }
    }, [setHotelGenericData])

    const fetchExcursion = useCallback(async (id: string) => {
        setLoading(true)
        try {
            const response = await agent.ExcursionDetailAPI.get(id)
            if (response?.status === true && response?.data?.[0]) {
                setExcursion(response.data[0])
            } else {
                showError(getErrorMessage(response?.error, "Failed to load excursion"))
                setExcursion(null)
            }
        } catch (e) {
            console.error("GetExcursionDetail Error", e)
            showError("Failed to load excursion")
            setExcursion(null)
        } finally {
            setLoading(false)
        }
    }, [showError])

    useEffect(() => {
        fetchGeneric()
    }, [fetchGeneric])

    useEffect(() => {
        if (!excursion_id) return
        if (excursion_id === "new") {
            setExcursion(getEmptyExcursion(hotelGenericData.category))
            setLoading(false)
        } else {
            fetchExcursion(excursion_id)
        }
    }, [excursion_id, fetchExcursion, hotelGenericData.category])

    // When generic categories load after "new" was opened, patch category_id from 0 to real ids //
    useEffect(() => {
        if (excursion_id !== "new" || !excursion || !hotelGenericData.category?.length) return
        const hasZero = [
            ...(excursion.excursion_pricing ?? []),
            ...(excursion.child_policy ?? []),
            ...(excursion.excursion_add_ons ?? []).flatMap((a) => a.add_on_pricing ?? []),
        ].some((r) => (r as { category_id?: number }).category_id === 0)
        if (hasZero) {
            setExcursion(patchExcursionCategoryIds(excursion, hotelGenericData.category))
        }
    }, [excursion_id, excursion, hotelGenericData.category])

    const updateExcursion = useCallback((updates: Partial<ExcursionDetailModel>) => {
        setExcursion((prev) => (prev ? { ...prev, ...updates } : null))
    }, [])

    const handleBack = () => {
        navigate(-1)
    }

    const handleSave = async () => {
        if (!excursion) return
        const errors = validateExcursion(excursion)
        setValidationErrors(errors)
        if (hasValidationErrors(errors)) {
            const firstMessage = Object.values(errors)[0]
            showError(firstMessage ?? "Please fix validation errors")
            return
        }
        try {
            const isNew = excursion_id === "new"
            const categories = hotelGenericData?.category ?? []
            const dataToSave =
                categories.length > 0
                    ? patchExcursionCategoryIds(excursion, categories)
                    : excursion
            const payload = toPutPayload(dataToSave, isNew)
            const response = await agent.UpdateExcursionAPI.put(payload)
            if (response?.status === true) {
                setValidationErrors({})
                showSuccess("Excursion saved successfully")
                if (excursion_id === "new" && response?.data?.[0]?.excursion_id) {
                    navigate(`/excursions/${response.data[0].excursion_id}`, { replace: true })
                }
            } else {
                showError(getErrorMessage(response?.error, "Failed to save excursion"))
            }
        } catch (e) {
            console.error("Save excursion Error", e)
            showError("Failed to save excursion")
        }
    }

    if (loading && excursion_id !== "new") {
        return (
            <div style={{ width: screenSize, margin: "24px auto", textAlign: "center", padding: 48 }}>
                Loading...
            </div>
        )
    }

    if (!excursion_id || (!excursion && excursion_id !== "new")) {
        return null
    }

    const displayExcursion = excursion || getEmptyExcursion()

    return (
        <div style={{ width: screenSize, margin: "24px auto" }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 8 }} style={{ minWidth: 0 }}>
                    <h1
                        className={excursionDetailStyles.excursionDetailPageTitle}
                        title={excursion_id === "new" ? undefined : displayExcursion.excursion_name}
                    >
                        {excursion_id === "new" ? "Excursion Detail" : toTitleCase(displayExcursion.excursion_name)}
                    </h1>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }} style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button type="outlined" label="<- Back" style={{ marginRight: "16px" }} onClick={handleBack} />
                    <Button type="contained" label="Save" onClick={handleSave} />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <ExcursionBasicDetail
                        excursion={displayExcursion}
                        cityOptions={getActiveCityOptions(hotelGenericData.city, displayExcursion.city_id)}
                        // typeOptions={hotelGenericData.excursion_type.map((x) => ({
                        //     label: x.excursion_type_name,
                        //     value: String(x.excursion_type_id),
                        // }))}
                        onChange={updateExcursion}
                        errors={{
                            excursion_name: validationErrors.excursion_name,
                            city: validationErrors.city,
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <ExcursionBlackOutDate
                        excursion={displayExcursion}
                        onChange={updateExcursion}
                        errors={{
                            blackout: validationErrors.blackout,
                            blackout_overlap: validationErrors.blackout_overlap,
                            unavailable_days: validationErrors.unavailable_days,
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }} style={{ display: "flex" }}>
                    <ExcursionChildPolicy
                        excursion={displayExcursion}
                        onChange={updateExcursion}
                        error={validationErrors.child_policy_max_age}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }} style={{ display: "flex" }}>
                    <ExcursionPricing
                        excursion={displayExcursion}
                        onChange={updateExcursion}
                        error={validationErrors.pricing_base_price}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <ExcursionAddOns
                        excursion={displayExcursion}
                        onChange={updateExcursion}
                        error={validationErrors.add_ons}
                    />
                </Grid>
            </Grid>
        </div>
    )
}

export default ExcursionDetail
